import { useState } from "react";
import {
  Button,
  TextField,
  Box,
  Typography,
  Paper,
  IconButton,
  Chip,
  Alert,
  AlertTitle,
  Divider,
} from "@mui/material";
import {
  CheckCircle2,
  AlertTriangle,
  XCircle,
  ChevronRight,
  Edit3,
  Check,
  X,
} from "lucide-react";

const FIELD_LABELS = {
  cognome: "Cognome",
  nome: "Nome",
  data_nascita: "Data di nascita",
  luogo_nascita: "Luogo di nascita",
  codice_fiscale: "Codice Fiscale",
  sesso: "Sesso",
  tipo_documento: "Tipo documento",
  numero_documento: "Numero documento",
  data_rilascio: "Data rilascio",
  localita_rilascio: "Luogo rilascio",
  ente_rilascio: "Ente rilascio",
  cittadinanza: "Cittadinanza",
  cittadinanza_altra: "Nazionalità",
  residenza_indirizzo: "Indirizzo",
  residenza_localita: "Comune",
};

const FIELD_SECTIONS = [
  {
    title: "👤 Dati Anagrafici",
    fields: [
      "cognome",
      "nome",
      "data_nascita",
      "luogo_nascita",
      "codice_fiscale",
      "sesso",
    ],
  },
  {
    title: "📄 Documento di Identità",
    fields: [
      "tipo_documento",
      "numero_documento",
      "data_rilascio",
      "localita_rilascio",
      "ente_rilascio",
    ],
  },
  {
    title: "🌍 Cittadinanza",
    fields: ["cittadinanza", "cittadinanza_altra"],
  },
  {
    title: "🏠 Residenza (suggerita)",
    fields: ["residenza_indirizzo", "residenza_localita"],
  },
];

function confidenceLabel(score) {
  if (score === undefined || score === null) return null;
  if (score >= 0.8)
    return { label: "Alta", color: "success", icon: CheckCircle2 };
  if (score >= 0.5)
    return { label: "Media", color: "warning", icon: AlertTriangle };
  return { label: "Bassa", color: "error", icon: XCircle };
}

function getConfidenceKey(field) {
  const map = {
    cognome: "last_name",
    nome: "first_name",
    data_nascita: "birth_date",
    luogo_nascita: "birth_place",
    codice_fiscale: "tax_code",
    sesso: "gender",
    numero_documento: "document_number",
    data_rilascio: "issue_date",
    localita_rilascio: "issue_place",
    ente_rilascio: "issuing_authority",
  };
  return map[field] || field;
}

function FieldRow({ fieldKey, value, confidence, onEdit }) {
  const [editing, setEditing] = useState(false);
  const [localVal, setLocalVal] = useState(value || "");
  const conf = confidenceLabel(confidence);
  const Icon = conf?.icon;

  function handleSave() {
    onEdit(fieldKey, localVal);
    setEditing(false);
  }

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        py: 1.5,
        borderBottom: "1px solid",
        borderColor: "divider",
        "&:last-child": { borderBottom: 0 },
        gap: 2,
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontSize: "0.625rem", display: "block" }}
        >
          {FIELD_LABELS[fieldKey] || fieldKey}
        </Typography>
        {editing ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
            <TextField
              fullWidth
              size="small"
              value={localVal}
              onChange={(e) => setLocalVal(e.target.value)}
              autoFocus
              sx={{ "& .MuiInputBase-input": { fontSize: "0.75rem", py: 0.5 } }}
            />
            <IconButton size="small" onClick={handleSave} color="primary">
              <Check size={16} />
            </IconButton>
            <IconButton size="small" onClick={() => setEditing(false)}>
              <X size={16} />
            </IconButton>
          </Box>
        ) : (
          <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>
            {value || "—"}
          </Typography>
        )}
      </Box>
      <Box
        sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}
      >
        {conf && (
          <Chip
            size="small"
            icon={<Icon size={12} />}
            label={conf.label}
            color={conf.color}
            variant="outlined"
            sx={{
              fontSize: "0.625rem",
              height: 18,
              "& .MuiChip-icon": { ml: 0.5 },
            }}
          />
        )}
        {!editing && (
          <IconButton
            size="small"
            onClick={() => {
              setLocalVal(value || "");
              setEditing(true);
            }}
            sx={{
              color: "text.secondary",
              "&:hover": { color: "text.primary" },
            }}
          >
            <Edit3 size={14} />
          </IconButton>
        )}
      </Box>
    </Box>
  );
}

export default function ExtractionReview({ extraction, onConfirm, onRetry }) {
  const { extracted, formFields } = extraction;
  const [fields, setFields] = useState({ ...formFields });
  const confidence = extracted?.confidence || {};

  function handleEdit(key, val) {
    setFields((prev) => ({ ...prev, [key]: val }));
  }

  const hasWarnings = extracted?.warnings?.length > 0;
  const needsReview = extracted?.needs_manual_review;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <Box
        sx={{
          textAlign: "center",
          display: "flex",
          flexDirection: "column",
          gap: 1,
        }}
      >
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: 4,
            bgcolor: "success.light",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mx: "auto",
          }}
        >
          <CheckCircle2 color="white" />
        </Box>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Dati rilevati automaticamente
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Controlla le informazioni estratte e correggi eventuali errori
        </Typography>
      </Box>

      {hasWarnings && (
        <Alert severity="warning" sx={{ borderRadius: 3 }}>
          <AlertTitle sx={{ fontWeight: 700, fontSize: "0.75rem", mb: 0.5 }}>
            ⚠️ Avvisi
          </AlertTitle>
          {extracted.warnings.map((w, i) => (
            <Typography key={i} variant="caption" sx={{ display: "block" }}>
              {w}
            </Typography>
          ))}
        </Alert>
      )}

      {needsReview && (
        <Alert
          severity="info"
          sx={{
            borderRadius: 3,
            bgcolor: "#fff9db",
            border: "1px solid #ffe066",
            "& .MuiAlert-icon": { color: "#856404" },
          }}
        >
          <AlertTitle
            sx={{ fontWeight: 700, fontSize: "0.75rem", color: "#856404" }}
          >
            Verifica richiesta
          </AlertTitle>
          <Typography variant="caption" sx={{ color: "#856404" }}>
            Alcuni campi richiedono attenzione prima di proseguire.
          </Typography>
        </Alert>
      )}

      {FIELD_SECTIONS.map((section) => {
        const sectionFields = section.fields.filter(
          (f) =>
            fields[f] !== undefined && fields[f] !== null && fields[f] !== "",
        );
        if (sectionFields.length === 0) return null;
        return (
          <Paper
            key={section.title}
            variant="outlined"
            sx={{ p: 2, borderRadius: 3, bgcolor: "action.hover" }}
          >
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                color: "text.secondary",
                display: "block",
                mb: 1.5,
                textTransform: "uppercase",
                letterSpacing: 0.5,
              }}
            >
              {section.title}
            </Typography>
            {sectionFields.map((field) => (
              <FieldRow
                key={field}
                fieldKey={field}
                value={fields[field]}
                confidence={confidence[getConfidenceKey(field)]}
                onEdit={handleEdit}
              />
            ))}
          </Paper>
        );
      })}

      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ textAlign: "center", px: 2, fontSize: "0.625rem" }}
      >
        Puoi modificare qualsiasi valore toccando l'icona matita. Potrai
        correggere i dati anche nelle pagine successive.
      </Typography>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        <Button
          variant="contained"
          size="large"
          onClick={() => onConfirm(fields)}
          endIcon={<ChevronRight size={18} />}
          fullWidth
          sx={{ textTransform: "none", py: 1.5, borderRadius: 2.5 }}
        >
          Conferma e continua
        </Button>
        <Button
          variant="text"
          onClick={onRetry}
          fullWidth
          sx={{
            textTransform: "none",
            color: "text.secondary",
            fontSize: "0.875rem",
          }}
        >
          Ricarica documento
        </Button>
      </Box>
    </Box>
  );
}
